import {
  HttpClient,
  HttpRequestInit,
  HttpResponse,
  HttpUtilService,
} from '../../http/index';
import { GetNextEventProvider } from '../providers/get-next-event-provider';
import { Logger } from '../../../logger/logger';
import { StorageKeys } from '../../../constants/storage-key';
import { ApiUriConstants } from '../../../constants/api-uri-constants';
import { CXoneAuthBase } from '../../authenticate/cxone-auth-base';
import { CXoneIndicator, CustomFormData, StartSessionRequest } from '@nice-ccf/common-sdk';
import {
  CallContactEvent,
  MuteEvent,
  UpdatePermissionsEvent,
  AgentSessionEndEvent,
  AgentSessionStartEvent,
  AgentStateEvent,
  UpdateUnavailableCodeEvent,
  AgentLegEvent
} from '@nice-ccf/common-sdk';
import { Subject } from 'rxjs';

/**
 * Utility for agent session management
 */
export class AgentSessionManager {
  protected logger: Logger = new Logger('SDK');
  protected utilService: HttpUtilService = new HttpUtilService();
  private sessionId = '';
  private getNextEventProvider: GetNextEventProvider = <GetNextEventProvider>{};
  private _callContactEventSubject = new Subject<CallContactEvent>();
  private _muteEventSubject = new Subject<MuteEvent>();
  private _updatePermissionsEventSubject = new Subject<UpdatePermissionsEvent>();
  private _agentSessionEndSubject = new Subject<AgentSessionEndEvent>();
  private _agentSessionStartSubject = new Subject<AgentSessionStartEvent>();
  private _agentStateSubject = new Subject<AgentStateEvent>();
  private _agentUnavailableCodeSubject = new Subject<UpdateUnavailableCodeEvent>();
  private _agentLegSubject = new Subject<AgentLegEvent>();
  private _agentIndicatorsSubject = new Subject();
  private _contactIndicatorsSubject = new Subject<CXoneIndicator>();
  private static singleton: AgentSessionManager;
  public static cxoneAuth: CXoneAuthBase;
  public auth: CXoneAuthBase;

  /**
   * constructor to initialise various data members for the class
   * @example
   * ```
   * const agentSession = new AgentSessionManager(CXoneAuthBase);
   * ```
   */
  private constructor(authBase: CXoneAuthBase) {
    this.auth = authBase;
  }

  /**
   * @example -
   * ``` 
   * const callContactEventSubject  = agentSession.callContactEventSubject
   * ``` 
  */
  get callContactEventSubject() {
    return this._callContactEventSubject;
  }

  /**
   * @example -
   * ```
   * const muteEventSubject = agentSession.muteEventSubject
   * ``` 
   */
  get muteEventSubject() {
    return this._muteEventSubject;
  }

  /**
   * @example -
   * ```   
   * const updatePermissionsEventSubject = agentSession.updatePermissionsEventSubject
   * ``` 
   */
  get updatePermissionsEventSubject() {
    return this._updatePermissionsEventSubject;
  }

  /**
  * @example -
  * ```
  * const agentSessionEndSubject = agentSession.agentSessionEndSubject
  * ```
  */
  get agentSessionEndSubject() {
    return this._agentSessionEndSubject;
  }

  /**
   * @example
   * ```
   * const agentState = agentSession.agentStateSubject
   * ```
   */
  get agentStateSubject() {
    return this._agentStateSubject;
  }

  /**
   * @example
   * ```
   * const updateUnavailableCode = agentSession.agentUpdateUnavailableCodeSubject
   * ```
   */
  get agentUpdateUnavailableCodeSubject() {
    return this._agentUnavailableCodeSubject;
  }

  /**
  * @example -
  * ```
  * const agentSessionStartSubject = agentSession.agentSessionStartSubject
  * ```
  */
  get agentSessionStartSubject() {
    return this._agentSessionStartSubject;
  }

  /**
   * @example -
   * ```
   * const agentLegSubject = agentSession.agentLegSubject
   * ```
   */
  get agentLegSubject() {
    return this._agentLegSubject;
  }

  /**
   * @example -
   * ```
   * const agentIndicatorsSubject = agentSession.agentIndicatorsSubject
   * ```
   */
  get agentIndicatorsSubject() {
    return this._agentIndicatorsSubject;
  }

  /**
   * @example -
   * ```
   * const contactIndicatorsSubject = agentSession.contactIndicatorsSubject
   * ```
   */
  get contactIndicatorsSubject() {
    return this._contactIndicatorsSubject;
  }

  /**
   * Method to create singleton object of the class
   * @example
   * ```
   * const agentSession = AgentSessionManager.instance();
   * ```
   */
  public static get instance(): AgentSessionManager {
    if (!AgentSessionManager.singleton) {
      this.cxoneAuth = CXoneAuthBase.Instance;
      AgentSessionManager.singleton = new AgentSessionManager(this.cxoneAuth);
    }
    return AgentSessionManager.singleton;
  }

  /**
   * Method to start session for an agent
   * @param startSessionRequest - Object of type StartSessionRequest.
   * @returns - response from the start agent session api
   * @example
   * ```
   * const startAgentSession = this.startSession(startSessionRequest)
   * ```
   */
  startSession(
    startSessionRequest: StartSessionRequest
  ): Promise<HttpResponse> {
    const baseUri = this.auth.getCXoneConfig().acdApiBaseUri;
    const endpoint = baseUri + ApiUriConstants.AGENT_SESSION_URI;
    return new Promise<HttpResponse>((resolve, reject) => {
      const reqInit: HttpRequestInit = {
        headers: this.utilService.initHeader(
          this.auth.getAuthToken().accessToken,
          ''
        ).headers,
        body: startSessionRequest,
      };
      HttpClient.post(endpoint, reqInit)
        .then((resp: HttpResponse) => {
          this.logger.info(
            'startSession',
            `[AgentSession][StartSession] status: ${resp.status}, statusTest: ${resp.statusText}, data: ${resp.data}`
          );
          if (resp.data.sessionId) {
            localStorage.setItem(
              StorageKeys.ACD_SESSION_ID,
              resp.data.sessionId
            );
            this.sessionId = resp.data.sessionId;
            //initiate get next event
            this.getNextEventProvider = GetNextEventProvider.instance;
            this.getNextEventProvider.startGetNextEvents();
            resolve(resp);
          } else {
            reject(resp);
          }
        })
        .catch((err: HttpResponse) => {
          this.logger.error(
            'startSession',
            `[AgentSession][StartSession] status: ${err.status}, statusTest: ${err.statusText}, data: ${err.data}`
          );
          reject(err);
        });
    });
  }

  /**
   * Method to join session for an agent
   * @param options - input parameters for the method includes agent id
   * @returns - response from the join agent session api
   * @example
   * ```
   * const agentObj = {agentId: 23}
   * const joinAgentSession = this.joinSession(agentObj)
   * ```
   */
  joinSession(options?: { agentId: number }): Promise<HttpResponse> {
    const baseUri = this.auth.getCXoneConfig().acdApiBaseUri;
    const endpoint = baseUri + ApiUriConstants.JOIN_AGENT_SESSION_URI;
    return new Promise<HttpResponse>((resolve, reject) => {
      const reqInit: HttpRequestInit = {
        headers: this.utilService.initHeader(
          this.auth.getAuthToken().accessToken,
          ''
        ).headers,
        body: {
          asAgentId: options?.agentId ? options.agentId : '',
        },
      };
      HttpClient.post(endpoint, reqInit)
        .then((resp: HttpResponse) => {
          this.logger.info(
            'joinSession',
            `[AgentSession][JoinSession] status: ${resp.status}, statusTest: ${resp.statusText}, data: ${resp.data}`
          );
          if (resp.data.sessionId) {
            localStorage.setItem(
              StorageKeys.ACD_SESSION_ID,
              resp.data.sessionId
            );
            this.sessionId = resp.data.sessionId;
            //initiate get next event
            this.getNextEventProvider = GetNextEventProvider.instance;
            this.getNextEventProvider.startGetNextEvents();
            resolve(resp);
          } else {
            reject(resp);
          }
        })
        .catch((err: HttpResponse) => {
          this.logger.error(
            'joinSession',
            `[AgentSession][JoinSession] status: ${err.status}, statusTest: ${err.statusText}, data: ${err.data}`
          );
          reject(err);
        });
    });
  }

  /**
   * Method to end an agent session
   * @param forceLogoff - boolean values forces agent to end session
   * @param endContacts - boolean value indicating if an existing contact should end
   * @param ignorePersonalQueue - boolean indicating the agent session to end, regardless of the personal queue
   * @returns - response to end session of an agent
   * @example
   * ```
   * const angentEndSession = this.endSession(true, false, ture);
   * ```
   */
  endSession(
    forceLogoff: boolean,
    endContacts: boolean,
    ignorePersonalQueue: boolean
  ): Promise<HttpResponse> {
    const baseUri = this.auth.getCXoneConfig().acdApiBaseUri;
    const endpoint =
      baseUri + ApiUriConstants.AGENT_SESSION_URI + '/' + this.sessionId;
    return new Promise<HttpResponse>((resolve, reject) => {
      const reqInit: HttpRequestInit = {
        headers: this.utilService.initHeader(
          this.auth.getAuthToken().accessToken,
          ''
        ).headers,
        body: {
          forceLogoff: forceLogoff,
          endContacts: endContacts,
          ignorePersonalQueue: ignorePersonalQueue,
        },
      };
      HttpClient.delete(endpoint, reqInit)
        .then((resp: HttpResponse) => {
          this.logger.info(
            'endSession',
            `[AgentSession][endSession] status: ${resp.status}, statusTest: ${resp.statusText}, data: ${resp.data}`
          );
          if (resp.data) {
            resolve(resp);
            localStorage.removeItem(StorageKeys.ACD_SESSION_ID);
            this.sessionId = '';
            //terminate get next event
            this.getNextEventProvider = GetNextEventProvider.instance;
            this.getNextEventProvider.terminateUtilWorker();
          } else {
            reject(resp);
          }
        })
        .catch((err: HttpResponse) => {
          this.logger.error(
            'joinSession',
            `[AgentSession][endSession] status: ${err.status}, statusTest: ${err.statusText}, data: ${err.data}`
          );
          reject(err);
        });
    });
  }

  /**
   * Posts custom form data 
   * @param contactId - sendor contact id
   * @param customFormData - custom form data with '|' delimited string for each key/value 
   * @returns - response for custom data api
   * @example
   * ```
   * this.postCustomFormData('1234', customFormData);
   * ```
   */
   postCustomFormData = (contactId: string, customFormData: CustomFormData) => {
    const sessionId = this.getSessionId();
    const postCustomDataEndpoint = ApiUriConstants.POST_CUSTOM_FORM_DATA.replace(
      '{sessionId}',
      sessionId
    ).replace('{contactId}', contactId);

    const baseUri = this.auth.getCXoneConfig().acdApiBaseUri;
    const endpoint = baseUri + postCustomDataEndpoint;
    const reqInit: HttpRequestInit = {
      headers: this.utilService.initHeader(
        this.auth.getAuthToken().accessToken,
        ''
      ).headers,
      body: customFormData,
    };

    return new Promise<HttpResponse>((resolve, reject) => {
      HttpClient.post(endpoint, reqInit)
        .then((resp: HttpResponse) => {
          this.logger.info('postCustomFormData', 'Post custom form success');
          resolve(resp);
        })
        .catch((error: HttpResponse) => {
          this.logger.error(
            'postCustomFormData',
            'Post custom form failed :-' + error.toString()
          );
          reject(error);
        });
    });
  };

  /**
   *
   * @returns - agent session id
   * @example
   * ```
   * const agentSessionId = this.getSessionId();
   * ```
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   *
   * @param value - updatedSessionId
   * @example
   * ```
   * this.setSessionId('SHRxbEd2ZkFyN');
   * ```
   */
  setSessionId(value: string) {
    this.sessionId = value;
  }
}
